import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { getSubcontractByMissionId, getMissionMobileData, uploadMissionDocument, downloadSubcontractContract } from '../../services/missionService';
import { getFileUrl } from '../../config';
import { deleteChauffeurDocument } from '../../services/chauffeurService';
import {
    Alert,
    Spinner,
    Button,
    Card,
    Table,
    Row,
    Col,
    Badge,
    Form
} from 'react-bootstrap';
import {
    FaUserTie,
    FaCar,
    FaArrowLeft,
    FaCheckCircle,
    FaRoute,
    FaFileInvoiceDollar,
    FaInfoCircle,
    FaHandshake,
    FaFileAlt,
    FaUpload,
    FaTrash,
    FaDownload,
    FaEye,
    FaPrint
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import { useI18n } from '../../i18n/I18nContext';
import './ChauffeurForm.css'; // Re-use the document card styles
import './MissionTimeline.css';

const STATUS_VARIANTS = (t) => ({
    EN_COURS: { badge: 'primary', text: t('missions.status.en_cours') },
    EN_ATTENTE: { badge: 'warning', text: t('missions.status.en_attente') },
    TERMINEE: { badge: 'success', text: t('missions.status.terminee') },
    ANNULEE: { badge: 'danger', text: t('missions.status.annulee') }
});

const SubcontractedMissionDetail = () => {
    const { missionId } = useParams();
    const navigate = useNavigate();
    const { t } = useI18n();

    const [subcontract, setSubcontract] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        const fetchDetail = async () => {
            try {
                setLoading(true);
                const data = await getSubcontractByMissionId(missionId);
                setSubcontract(data);

                // Fetch mobile data for documents
                fetchDocuments();
            } catch (err) {
                setError(err.message);
                toast.error(t('subcontracting.toast.loadDetailsError'));
            } finally {
                setLoading(false);
            }
        };

        if (missionId) {
            fetchDetail();
        }
    }, [missionId]);

    const fetchDocuments = async () => {
        try {
            setLoadingDocs(true);
            const mobileData = await getMissionMobileData(missionId);
            setDocuments(mobileData.documents || []);
        } catch (err) {
            console.error("Error fetching documents:", err);
        } finally {
            setLoadingDocs(false);
        }
    };

    const handleUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            setIsUploading(true);
            await uploadMissionDocument(missionId, file, {
                name: file.name,
                type: 'MISSION_DOCUMENT'
            });
            toast.success(t('common.success'));
            fetchDocuments();
        } catch (err) {
            toast.error(t('common.error') + ": " + err.message);
        } finally {
            setIsUploading(false);
            // Reset input
            event.target.value = '';
        }
    };

    const handleDeleteDoc = async (docId) => {
        if (!window.confirm(t('common.confirmDelete') || "Voulez-vous vraiment supprimer ce document ?")) return;

        try {
            await deleteChauffeurDocument(docId);
            toast.success(t('common.success'));
            setDocuments(prev => prev.filter(d => d.id !== docId));
        } catch (err) {
            toast.error(t('common.error') + ": " + err.message);
        }
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleString(t('common.dashboard') === 'Dashboard' ? 'en-US' : 'fr-FR');
    };

    const formatCurrency = (amount, currency) => {
        const validCurrency = currency || 'EUR';
        return new Intl.NumberFormat(t('common.dashboard') === 'Dashboard' ? 'en-US' : 'fr-FR', {
            style: 'currency',
            currency: validCurrency
        }).format(amount || 0);
    };

    const handlePrintContract = async () => {
        try {
            const blob = await downloadSubcontractContract(mission.id);
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
            // Note: We don't revoke the object URL immediately if we open it in a new tab, 
            // as it might needed for loading. Ideally we'd manage lifecycle, but for this use case 
            // valid for session lifetime is usually okay or we can use a timeout.
            setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        } catch (err) {
            console.error(err);
            toast.error(t('common.error'));
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
                    <Spinner animation="border" variant="primary" />
                </div>
            </DashboardLayout>
        );
    }

    if (error || !subcontract) {
        return (
            <DashboardLayout>
                <div className="container py-4">
                    <Button variant="link" onClick={() => navigate('/subcontracted-missions')} className="text-decoration-none mb-3">
                        <FaArrowLeft className="me-2" /> {t('common.back')}
                    </Button>
                    <Alert variant="danger">{error || t('missions.errors.missionNotFound')}</Alert>
                </div>
            </DashboardLayout>
        );
    }

    const { mission, subcontractor } = subcontract;
    const statusConfig = STATUS_VARIANTS(t)[mission.status] || { badge: 'secondary', text: mission.status };

    const heroStat = (label, value) => (
        <div key={label} className="d-flex flex-column">
            <small className="text-uppercase text-muted fw-semibold small">{label}</small>
            <span className="fw-semibold text-dark">{value || '—'}</span>
        </div>
    );

    return (
        <DashboardLayout>
            <ToastContainer />
            <div className="container-fluid py-4">
                <Button variant="link" onClick={() => navigate('/subcontracted-missions')} className="text-decoration-none mb-4 p-0 text-muted">
                    <FaArrowLeft className="me-2" /> {t('subcontracting.missionsPages.detail.backToList')}
                </Button>

                {/* Mission Hero - Same as AssignMission */}
                <Card className="border-0 shadow-sm mb-4 rounded-4 overflow-hidden">
                    <div style={{ height: 6, background: 'linear-gradient(90deg, #0d6efd, #6f42c1)' }} />
                    <Card.Body className="p-4">
                        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3">
                            <div className="flex-grow-1">
                                <small className="text-uppercase text-muted fw-semibold">{t('subcontracting.missionsPages.detail.sections.mission')}</small>
                                <h2 className="fw-bold mb-1 text-dark">
                                    {mission.referenceNumber || `${t('common.mission')} #${mission.id.substring(0, 8)}`}
                                </h2>
                                <div className="d-flex align-items-center flex-wrap gap-2 text-muted">
                                    <FaRoute className="me-2 text-primary" />
                                    <span className="fw-semibold text-dark">
                                        {mission.departureLocation}
                                        <span className="mx-2 text-muted">→</span>
                                        {mission.arrivalLocation}
                                    </span>
                                </div>
                            </div>
                            <Badge bg={statusConfig.badge} className="px-3 py-2 rounded-pill fw-normal">
                                {statusConfig.text}
                            </Badge>
                        </div>
                        <hr className="my-4" />
                        <div className="d-flex flex-wrap gap-4">
                            {heroStat(t('subcontracting.missionsPages.detail.fields.plannedDates'), `${new Date(mission.departureDate).toLocaleDateString(t('common.dashboard') === 'Dashboard' ? 'en-US' : 'fr-FR')} → ${new Date(mission.arrivalDate).toLocaleDateString(t('common.dashboard') === 'Dashboard' ? 'en-US' : 'fr-FR')}`)}
                            {heroStat(t('subcontracting.missionsPages.detail.fields.cargoType'), mission.cargoType)}
                            {heroStat(t('subcontracting.missionsPages.detail.fields.weightLength'), `${mission.weight || 0} t • ${mission.length || 0} m`)}
                        </div>

                        {/* Timeline / Progress Tracker */}
                        <div className="mission-timeline-container mt-4">
                            <h6 className="text-uppercase text-muted fw-bold small mb-4 px-2">
                                {t('missions.status.timeline.title') || 'Suivi de progression'}
                            </h6>
                            <div className="timeline-steps">
                                {[
                                    { label: t('missions.status.timeline.departure'), date: mission.departureDate, icon: <FaRoute />, done: true },
                                    { label: t('missions.status.timeline.arrivalLoading'), date: mission.loadingArrivalTime, icon: <FaCheckCircle />, done: !!mission.loadingArrivalTime },
                                    { label: t('missions.status.timeline.departureLoading'), date: mission.loadingDepartureTime, icon: <FaCheckCircle />, done: !!mission.loadingDepartureTime },
                                    { label: t('missions.status.timeline.arrivalDelivery'), date: mission.deliveryArrivalTime, icon: <FaCheckCircle />, done: !!mission.deliveryArrivalTime },
                                    { label: t('missions.status.timeline.completed'), date: (mission.status === 'TERMINEE' || mission.status === 'ANNULEE') ? mission.updatedAt : null, icon: <FaCheckCircle />, done: mission.status === 'TERMINEE' || mission.status === 'ANNULEE' }
                                ].map((step, idx, arr) => {
                                    const isNext = !step.done && (idx === 0 || arr[idx - 1].done);
                                    return (
                                        <React.Fragment key={idx}>
                                            <div className={`timeline-step ${step.done ? 'done' : ''} ${isNext ? 'active' : ''}`}>
                                                <div className="marker">
                                                    {step.icon}
                                                </div>
                                                <div className="step-info">
                                                    <span className="step-label">{step.label}</span>
                                                    <span className="step-time">
                                                        {step.date ? formatDateTime(step.date) : '—'}
                                                    </span>
                                                </div>
                                                {idx < arr.length - 1 && <div className="timeline-connector" />}
                                            </div>
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Row>
                    <Col lg={8}>
                        {/* Documents Section */}
                        <Card className="border-0 shadow-sm mb-4 rounded-4 overflow-hidden">
                            <Card.Header className="bg-white d-flex align-items-center justify-content-between py-3 px-4">
                                <div className="d-flex align-items-center">
                                    <FaFileAlt className="text-primary me-2" />
                                    <h5 className="mb-0 fw-bold">{t('subcontracting.missionsPages.detail.sections.documents')}</h5>
                                    <Badge bg="secondary" className="ms-2">{documents.length}</Badge>
                                </div>
                                <div>
                                    <input
                                        type="file"
                                        id="mission-doc-upload"
                                        className="d-none"
                                        onChange={handleUpload}
                                        disabled={isUploading}
                                    />
                                    <Button
                                        as="label"
                                        htmlFor="mission-doc-upload"
                                        variant="outline-primary"
                                        size="sm"
                                        className="rounded-pill px-3 mb-0 pointer"
                                        disabled={isUploading}
                                    >
                                        {isUploading ? <Spinner animation="border" size="sm" /> : <><FaUpload className="me-1" /> {t('common.upload')}</>}
                                    </Button>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-4">
                                {loadingDocs ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" variant="primary" />
                                    </div>
                                ) : documents.length === 0 ? (
                                    <div className="create-account-card text-center py-5 bg-light rounded-4 border-2 border-dashed" style={{ maxWidth: '53.75rem' }}>
                                        <div className="text-muted mb-2">{t('subcontracting.missionsPages.detail.documents.empty')}</div>
                                        <small className="text-muted">{t('subcontracting.missionsPages.detail.documents.emptyInfo')}</small>
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column gap-3">
                                        {documents.map((doc) => (
                                            <div
                                                key={doc.id}
                                                className="p-3 rounded-4 border bg-white shadow-sm-hover transition-all"
                                                style={{ borderStyle: 'solid', borderWidth: '0.0625rem' }}
                                            >
                                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                                    <div className="d-flex align-items-center overflow-hidden">
                                                        <div className="bg-light p-2 rounded-3 me-3 text-primary">
                                                            <FaFileAlt size={20} />
                                                        </div>
                                                        <div className="overflow-hidden">
                                                            <div className="fw-bold text-dark text-truncate" title={doc.name}>
                                                                {doc.name || t('common.unassigned')}
                                                            </div>
                                                            <small className="text-muted">{doc.createdAt ? formatDateTime(doc.createdAt) : '—'}</small>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex gap-2 align-items-center">
                                                        <Badge bg="light" text="dark" className="fw-normal border px-3 py-2 rounded-pill">
                                                            {doc.type === 'MISSION_DOCUMENT' ? t('missions.missionList.document') : doc.type || t('common.ref')}
                                                        </Badge>
                                                        <div className="ms-2 d-flex gap-1">
                                                            <a
                                                                href={getFileUrl(doc.fileUrl)}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="btn btn-sm btn-outline-primary rounded-circle p-2"
                                                                title="Ouvrir"
                                                                style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                            >
                                                                <FaEye size={14} />
                                                            </a>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger rounded-circle p-2"
                                                                onClick={() => handleDeleteDoc(doc.id)}
                                                                title="Supprimer"
                                                                style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                            >
                                                                <FaTrash size={14} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                        {/* Subcontractor Info */}
                        <Card className="border-0 shadow-sm mb-4 rounded-4 overflow-hidden">
                            <div style={{ height: 6, background: 'linear-gradient(90deg, #198754, #20c997)' }} />
                            <Card.Body className="p-4">
                                <div className="d-flex align-items-center mb-4">
                                    <FaHandshake className="me-2 text-success" />
                                    <h5 className="mb-0 fw-bold">{t('subcontracting.missionsPages.detail.sections.partner')}</h5>
                                </div>
                                <div className="d-flex align-items-center p-3 rounded-3 bg-light mb-4">
                                    <div className="bg-white rounded-circle p-3 shadow-sm me-3 text-success">
                                        <FaUserTie size={24} />
                                    </div>
                                    <div>
                                        <div className="fw-bold fs-5">{subcontractor.enterpriseName}</div>
                                        <div className="text-muted">{subcontractor.telephone} • {subcontractor.email}</div>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <h6 className="text-uppercase text-muted fw-bold small mb-3">{t('subcontracting.missionsPages.detail.sections.instructions')}</h6>
                                    <div className="p-3 border rounded-3 bg-light text-dark">
                                        {subcontract.instructions || t('subcontracting.missionsPages.detail.fields.noInstructions')}
                                    </div>
                                </div>

                                <div>
                                    <h6 className="text-uppercase text-muted fw-bold small mb-3">{t('subcontracting.missionsPages.detail.sections.vehicle')}</h6>
                                    <div className="d-flex align-items-center">
                                        <FaCar className="text-muted me-2" />
                                        <span className="fw-semibold">{subcontract.vehicleRequested || t('subcontracting.missionsPages.detail.fields.anyVehicle')}</span>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>

                        {/* Price Breakdown */}
                        <Card className="border-0 shadow-sm mb-4 rounded-4 overflow-hidden">
                            <Card.Header className="bg-white py-3 px-4 border-0">
                                <div className="d-flex align-items-center">
                                    <FaFileInvoiceDollar className="text-primary me-2" />
                                    <h5 className="mb-0 fw-bold">{t('subcontracting.missionsPages.detail.sections.price')}</h5>
                                </div>
                            </Card.Header>
                            <Card.Body className="px-4 pb-4 pt-0">
                                <div className="table-responsive">
                                    <Table borderless className="align-middle mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="small text-uppercase text-muted fw-bold">{t('common.description')}</th>
                                                <th className="small text-uppercase text-muted fw-bold text-end">{t('common.price')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {subcontract.priceLines.map((line, idx) => (
                                                <tr key={idx} className="border-bottom">
                                                    <td className="py-3">{line.description}</td>
                                                    <td className="py-3 text-end fw-bold">{formatCurrency(line.amount, subcontract.currency)}</td>
                                                </tr>
                                            ))}
                                            <tr className="bg-light shadow-sm rounded-3">
                                                <td className="py-3 fw-bold ps-3">{t('subcontracting.missionsPages.detail.price.total')}</td>
                                                <td className="py-3 text-end fw-bold fs-5 pe-3 text-success">
                                                    {formatCurrency(subcontract.subcontractingCost, subcontract.currency)}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={4}>
                        {/* Operational Snapshot - Same side as AssignMission */}
                        <Card className="border-0 shadow-sm mb-4 rounded-4 overflow-hidden">
                            <Card.Header className="bg-white py-3 px-4 border-0">
                                <div className="d-flex align-items-center">
                                    <FaInfoCircle className="text-primary me-2" />
                                    <h6 className="mb-0 fw-bold text-uppercase text-muted">{t('subcontracting.missionsPages.detail.sections.operational')}</h6>
                                </div>
                            </Card.Header>
                            <Card.Body className="px-4 pb-4 pt-0">
                                <div className="d-flex flex-column gap-3">
                                    <div className="p-3 rounded-3 bg-light">
                                        <small className="text-uppercase text-muted fw-bold small">{t('subcontracting.missionsPages.detail.operational.schedule')}</small>
                                        <div className="mt-2 d-flex justify-content-between">
                                            <span className="text-muted small">{t('common.departure')}</span>
                                            <span className="fw-bold small">{new Date(mission.departureDate).toLocaleString(t('common.dashboard') === 'Dashboard' ? 'en-US' : 'fr-FR')}</span>
                                        </div>
                                        <div className="mt-1 d-flex justify-content-between">
                                            <span className="text-muted small">{t('common.arrival')}</span>
                                            <span className="fw-bold small">{new Date(mission.arrivalDate).toLocaleString(t('common.dashboard') === 'Dashboard' ? 'en-US' : 'fr-FR')}</span>
                                        </div>
                                    </div>

                                    <div className="p-3 rounded-3 bg-light">
                                        <small className="text-uppercase text-muted fw-bold small">{t('subcontracting.missionsPages.detail.operational.resources')}</small>
                                        <div className="mt-2 d-flex flex-column gap-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <FaUserTie className="text-primary" size={14} />
                                                <span className="small fw-bold">{mission.assignedChauffeurName || t('common.unassigned')}</span>
                                            </div>
                                            {mission.assignedVehicleInfo && (
                                                <div className="d-flex align-items-center gap-2">
                                                    <FaCar className="text-primary" size={14} />
                                                    <span className="small">{mission.assignedVehicleInfo}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="p-3 rounded-3 bg-light">
                                        <small className="text-uppercase text-muted fw-bold small">{t('subcontracting.missionsPages.detail.operational.finances')}</small>
                                        <div className="mt-2 d-flex justify-content-between">
                                            <span className="text-muted small">{t('subcontracting.missionsPages.detail.operational.clientPrice')}</span>
                                            <span className="fw-bold small text-primary">{formatCurrency(mission.price, mission.currency)}</span>
                                        </div>
                                        <div className="mt-1 d-flex justify-content-between">
                                            <span className="text-muted small">{t('subcontracting.missionsPages.detail.operational.freightCost')}</span>
                                            <span className="fw-bold small text-danger">-{formatCurrency(subcontract.subcontractingCost, subcontract.currency)}</span>
                                        </div>
                                        <hr className="my-2" />
                                        <div className="d-flex justify-content-between">
                                            <span className="fw-bold small">{t('subcontracting.missionsPages.detail.operational.grossMargin')}</span>
                                            <span className="fw-bold small text-success">
                                                {formatCurrency(mission.price - subcontract.subcontractingCost, mission.currency)}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="p-3 rounded-3 bg-light">
                                        <small className="text-uppercase text-muted fw-bold small">{t('subcontracting.missionsPages.detail.operational.delays')}</small>
                                        <div className="mt-2 d-flex justify-content-between">
                                            <span className="text-muted small">{t('common.status.pending')}</span>
                                            <span className="fw-bold small">{formatDateTime(subcontract.createdAt)}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <Button variant="outline-primary" className="w-100 rounded-pill mb-2" onClick={handlePrintContract}>
                                        <FaPrint className="me-2" />
                                        {t('subcontracting.missionsPages.detail.actions.printContract')}
                                    </Button>
                                    <Button variant="primary" className="w-100 rounded-pill" onClick={() => navigate(`/missions/edit/${mission.id}`)}>
                                        {t('subcontracting.missionsPages.detail.actions.editMission')}
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>
        </DashboardLayout>
    );
};

export default SubcontractedMissionDetail;
